// CEM Features
export const CEM_FEATURE_PAYMENT_PAY_INVOICES = 'Payment-Pay Invoices'
export const CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS =
  'Payment-View Invoices and Documents'
export const CEM_FEATURE_PAYMENT_CREATE_INSTALLMENTS =
  'Payment-Create Installments'
export const CEM_FEATURE_PAYMENT_CLIENT_ADMIN = 'Payment-Client Admin'
export const CEM_FEATURE_PAYMENT_ACCOUNT_MANAGEMENT =
  'Payment-Account Management'
export const CEM_FEATURE_PAYMENT_INST_ADDL_FREQUENCY_OPTS =
  'Payment-Inst Addl Frequency Opts'
export const CEM_FEATURE_PAYMENT_EXTENDED_INSTALLMENTS =
  'Payment-Extended Installments'
export const CEM_FEATURE_WELCOME_OPT_OUT = 'Welcome Opt Out'
export const CEM_FEATURE_ARTICLESHARE = 'Insights Article Sharing'
export const CEM_FEATURE_QUESTIONNAIRE_ASSIGN = 'Questionnaire-Assign'
export const CEM_FEATURE_QUESTIONNAIRE_EXTERNAL_COMPLETE =
  'Questionnaire-Complete'
export const CEM_FEATURE_MANAGE_ADMIN_SETTINGS = 'ManageAdminSettings'
export const CEM_FEATURE_CLIENT_ADMIN = 'ClientAdmin'

// User Preferences
export const USER_PREF_INVOICING_IGNOREUNRELATED = 'Invoicing_IgnoreUnrelated'

// Invoice Filter Statuses
export const INVOICE_FILTER_STATUS_OPEN = 'open'
export const INVOICE_FILTER_STATUS_PAID = 'paid'
export const INVOICE_FILTER_STATUS_CANCELLED = 'cancelled'
export const INVOICE_FILTER_STATUS_SCHEDULED = 'scheduled'

// Invoicing Features
export const INVOICING_FEATURE_AUTOPAY = 'Invoicing_Autopay'
export const INVOICING_FEATURE_EXTERNALEMAILALERTS =
  'Invoicing_ExternalEmailAlerts'
// Feature Flag constants
export type FeatureFlag =
  | 'AEMFeedFromBlobStorage'
  | 'AEMFeedFromConfigOverride'
  | 'BrandedAppsToolsList'
  | 'Communications'
  | 'DocumentRequest'
  | 'ECP_Redirect'
  | 'EmailUnsubscribe'
  | 'FAQRedesign'
  | 'HideWelcomeExperience'
  | 'HomePageEngagements'
  | 'InsightArticle_BannerImage'
  | 'InsightsFeedNotifications'
  | 'InsightsNavigation'
  | 'Invoicing'
  | 'Invoicing_Autopay'
  | 'Invoicing_AutopayCancel'
  | 'Invoicing_ExportTableData'
  | 'Notifications'
  | 'Questionnaires'
  | 'SpotlightArticleBrandEvolution'
  | 'Support'
  | 'TeamDocs2024External'
  | 'TeamDocs2024Internal'
  | 'UI_Redesign'
  | 'UserProfile'
  | 'UserProfile_Redesign'
  | 'BI_Reporting'
  | 'Targeting_Engagement'
  | 'Targeting_SharedArticle'
  | 'Invoicing_ExternalEmailAlerts'
  | 'AdminSettings'
  | 'UserManagement'

export type SignalRMethods = 'NotificationsNewCount' | 'NotificationsDelete'

export enum AdminSettingKey {
  SpotlightArticleId = 'SpotlightArticleUrl',
  ToolTileExclusion = 'REACT_APP_DISALLOWED_TOOLS',
}

// Admin Settings Keys
export const ADMIN_SETTINGS_POWER_BI_REPORTS_KEY = 'powerbidefinition'
export const ADMIN_SETTINGS_POWER_BI_REPORT_INSTANCES_KEY =
  'powerbireportinstances'

// PBI Report Definition Columns (for PBIReportDefinition table)
// The backend API uses these column names for sorting and field mapping
export const PBI_REPORT_COLUMNS = {
  REPORT_NAME: 'ReportName',
  SERVICE_PRINCIPAL_ID: 'ServicePrincipalId',
  IS_CLIENT_LEVEL_AUTHORIZATION: 'IsClientLevelAuthorization',
  CEM_APP_ID: 'CemappId',
  TENANT_ID: 'TenantId',
  CLIENT_SECRET_NAME: 'ClientSecretName',
  EFFECTIVE_IDENTITY_ENABLED: 'EffectiveIdentityEnabled',
  EFFECTIVE_IDENTITY_ROLE_NAME: 'EffectiveIdentityRoleName',
} as const

export type PbiReportColumn =
  (typeof PBI_REPORT_COLUMNS)[keyof typeof PBI_REPORT_COLUMNS]

// PBI Report Instance Columns (for PBIReportInstance table)
// Used for table column mapping and sorting in admin UI
export const PBI_INSTANCE_COLUMNS = {
  REPORT_DEFINITION_NAME: 'ReportDefinitionName',
  DISPLAY_NAME: 'DisplayName',
  CLIENT_ID: 'ClientId',
  CEM_ROLE_NAME: 'CemRoleName',
  IS_DASHBOARD: 'IsDashboard',
  PBI_WORKSPACE_ID: 'PbiWorkspaceId',
  PBI_OBJECT_ID: 'PbiObjectId',
} as const

export type PbiInstanceColumn =
  (typeof PBI_INSTANCE_COLUMNS)[keyof typeof PBI_INSTANCE_COLUMNS]

export const META_ROBOTS_MANAGED_BY = 'myRSM-search-indexing-guard'

// Map a string (e.g. from API sort key) to a known PbiReportColumn, or return original value if not found
export function mapPbiSortKey(val: string): PbiReportColumn | string {
  const key = (val ?? '').replace(/\s+/g, '').toLowerCase()

  const byCanonical = Object.values(PBI_REPORT_COLUMNS).find(
    (v) => v.replace(/\s+/g, '').toLowerCase() === key,
  )
  if (byCanonical) return byCanonical as PbiReportColumn

  // Additional mappings for case-insensitive matching
  if (key === 'cemappid') return PBI_REPORT_COLUMNS.CEM_APP_ID
  if (key === 'serviceprincipalid')
    return PBI_REPORT_COLUMNS.SERVICE_PRINCIPAL_ID
  if (key === 'isclientlevelauthorization')
    return PBI_REPORT_COLUMNS.IS_CLIENT_LEVEL_AUTHORIZATION
  if (key === 'effectiveidentityenabled')
    return PBI_REPORT_COLUMNS.EFFECTIVE_IDENTITY_ENABLED
  if (key === 'effectiveidentityrolename')
    return PBI_REPORT_COLUMNS.EFFECTIVE_IDENTITY_ROLE_NAME
  if (key === 'clientsecretname') return PBI_REPORT_COLUMNS.CLIENT_SECRET_NAME
  if (key === 'tenantid') return PBI_REPORT_COLUMNS.TENANT_ID
  if (key === 'reportname') return PBI_REPORT_COLUMNS.REPORT_NAME

  return val
}

// Map a string (e.g. from API sort key) to a known PbiInstanceColumn, or return original value if not found
export function mapPbiInstanceSortKey(val: string): PbiInstanceColumn | string {
  const key = (val ?? '').replace(/\s+/g, '').toLowerCase()

  const byCanonical = Object.values(PBI_INSTANCE_COLUMNS).find(
    (v) => v.replace(/\s+/g, '').toLowerCase() === key,
  )
  if (byCanonical) return byCanonical as PbiInstanceColumn

  // Additional mappings for case-insensitive matching
  if (key === 'reportdefinitionname')
    return PBI_INSTANCE_COLUMNS.REPORT_DEFINITION_NAME
  if (key === 'displayname') return PBI_INSTANCE_COLUMNS.DISPLAY_NAME
  if (key === 'clientid') return PBI_INSTANCE_COLUMNS.CLIENT_ID
  if (key === 'cemrolename') return PBI_INSTANCE_COLUMNS.CEM_ROLE_NAME
  if (key === 'isdashboard') return PBI_INSTANCE_COLUMNS.IS_DASHBOARD
  if (key === 'pbiworkspaceid') return PBI_INSTANCE_COLUMNS.PBI_WORKSPACE_ID
  if (key === 'pbiobjectid') return PBI_INSTANCE_COLUMNS.PBI_OBJECT_ID

  return val
}
